import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { K } from '../kabinet/atoms';
import { D } from '../kabinet/dobavki';
import { Подложка } from '../ui/modal';
import { IconChevron, IconClose, IconFolder, IconGear, IconPlus, IconTrash } from '../ui/icons';
import {
  кампанийСловами,
  поПорядку,
  подписьНовой,
  проверитьИмя,
  ИМЯ_ДО,
  type Группа,
} from '../rk/gruppy';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ГРУППЫ КАМПАНИЙ.
 *
 * Группа — свой список кампаний: отбор наверху таблицы показывает её
 * одну, итоги и общие действия считаются по показанному. Кампания
 * состоит в одной группе: если она была в другой, перейдёт в новую.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЧЕТЫРЕ МЕСТА, ОДНИ ДАННЫЕ
 *
 * Колонка слева от таблицы (на широком), кнопка выбора в ряду отборов
 * (на узком), меню в полосе выделения и окно настройки. Все читают
 * один и тот же запрос — иначе счёт «Рекомендации 3» в колонке
 * и «3 кампании» в окне разошлись бы после первой же перекладки.
 * ═══════════════════════════════════════════════════════════════════════
 */

export interface Группы {
  groups: Группа[];
  /** Сколько кампаний в кабинете всего — для строки «Все кампании». */
  всего: number;
}

export function useГруппы(base: string) {
  return useQuery({
    queryKey: ['группы', base],
    queryFn: () => api<Группы>(`${base}/groups`),
    enabled: base !== '',
    retry: false,
    staleTime: 60_000,
  });
}

/** Перечитать группы после любой правки: счётчики стоят в трёх местах. */
function useОбновить(base: string) {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['группы', base] });
}

/**
 * Колонка групп — слева от таблицы, на широком экране.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СВОРАЧИВАЕТСЯ И ПОМНИТ ЭТО. Двести шестнадцать пикселей — это две
 * колонки таблицы; тому, кто группами не пользуется, они не нужны,
 * а разворачивать колонку при каждом входе он не станет — он просто
 * решит, что таблица стала уже.
 *
 * ПАМЯТЬ — В `localStorage`, а не на сервере: это свойство ЭКРАНА,
 * а не кабинета. Один и тот же человек за настольным и за ноутбуком
 * хочет разного, и синхронизировать это между устройствами значило бы
 * менять ему вид там, где он его не трогал.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function КолонкаГрупп({
  base,
  className,
  выбрана,
  выбрать,
  свернуть,
}: {
  base: string;
  /** Чем прятать на узком: класс кладётся на сам `aside`. */
  className?: string;
  выбрана: string | null;
  выбрать(id: string | null): void;
  /** Нажали «свернуть» в шапке колонки. Состояние живёт снаружи. */
  свернуть(): void;
}) {
  const q = useГруппы(base);
  const [поиск, setПоиск] = useState('');
  const [окно, setОкно] = useState(false);

  if (q.data === undefined) return null;

  const группы = поПорядку(q.data.groups);
  const безГруппы = q.data.всего - группы.reduce((a, г) => a + г.count, 0);

  /* КОЛОНКИ НЕТ ВОВСЕ, ПОКА НЕТ ГРУПП. Пустая колонка с одной строкой
     «Все кампании» отнимает место и ничего не сообщает: заводят
     группы из полосы выделения, когда есть что складывать. */
  if (группы.length === 0) return null;

  /* ПОИСК ПОЯВЛЯЕТСЯ, КОГДА ГРУПП БОЛЬШЕ ВОСЬМИ — так в эталоне.
     На пяти он лишний: их видно все разом, и поле только отнимает
     строку. На двадцати без него не найти. */
  const видимые =
    поиск.trim() === ''
      ? группы
      : группы.filter((г) => г.name.toLowerCase().includes(поиск.trim().toLowerCase()));

  return (
    <aside data-d="колонка-групп" className={соединить(K.колонкаГрупп, className)}>
      <div className={K.шапкаКолонкиГрупп}>
        <b className={K.названиеКолонкиГрупп}>Группы</b>
        <span className={K.счётКолонкиГрупп}>{группы.length}</span>
        <button
          type="button"
          data-d="кнопка-свернуть-группы"
          className={K.кнопкаСвернутьГруппы}
          title="Свернуть колонку"
          aria-label="Свернуть колонку"
          onClick={свернуть}
        >
          <IconChevron size={14} dir="left" />
        </button>
      </div>

      {группы.length > ПОИСК_ОТ && (
        <span className={K.рядПоискаГрупп}>
          <input
            data-d="поиск-групп"
            className={K.поискГрупп}
            placeholder="Найти группу"
            value={поиск}
            onChange={(e) => setПоиск(e.target.value)}
          />
        </span>
      )}

      <div data-scroll="1" className={K.списокГрупп}>
        <СтрокаГруппы
          имя="Все кампании"
          счёт={q.data.всего}
          выбрана={выбрана === null}
          жать={() => выбрать(null)}
        />
        {видимые.map((г) => (
          <СтрокаГруппы
            key={г.id}
            имя={г.name}
            счёт={г.count}
            выбрана={выбрана === г.id}
            жать={() => выбрать(г.id)}
          />
        ))}

        {/* ═══════════════════════════════════════════════════════════
            «БЕЗ ГРУППЫ» — ПОСЛЕДНЕЙ СТРОКОЙ, и она не украшение.

            Так в эталоне. Разложить сорок кампаний по группам за один
            присест невозможно; вопрос «а что я ещё не разобрал»
            возникает на второй день и остаётся навсегда. Без этой
            строки ответить на него нечем: «Все кампании» показывают
            и разобранные тоже.

            ПРЯЧЕТСЯ, КОГДА РАЗОБРАНО ВСЁ: строка «Без группы 0» —
            это строка, которая ничего не покажет по нажатию. */}
        {безГруппы > 0 && (
          <СтрокаГруппы
            имя="Без группы"
            счёт={безГруппы}
            выбрана={выбрана === БЕЗ_ГРУППЫ}
            жать={() => выбрать(БЕЗ_ГРУППЫ)}
          />
        )}
      </div>

      <div className={K.подвалКолонкиГрупп}>
        <button
          type="button"
          data-d="кнопка-новая-группа"
          className={K.кнопкаНоваяГруппа}
          onClick={() => setОкно(true)}
        >
          <IconPlus size={13} /> Новая группа
        </button>
        <button
          type="button"
          data-d="кнопка-настроить-группы"
          className={K.кнопкаНастроитьГруппы}
          onClick={() => setОкно(true)}
        >
          <IconGear size={13} /> Переименовать и удалить
        </button>
      </div>

      {окно && <ОкноГрупп base={base} закрыть={() => setОкно(false)} />}
    </aside>
  );
}

/**
 * «Без группы» — не опознаватель, а особый отбор.
 *
 * ОТДЕЛЬНОЙ СТРОКОЙ, А НЕ `null`: `null` уже занят и означает
 * «все кампании». Строка выбрана нарочно непохожей на опознаватель
 * (`uuid`), чтобы её нельзя было принять за настоящую группу
 * ни на глаз, ни по ошибке в сравнении.
 */
export const БЕЗ_ГРУППЫ = 'без-группы';

const ПАМЯТЬ = 'bidra.группы.свёрнуты';
/** Больше этого — показываем поиск. Так в эталоне. */
const ПОИСК_ОТ = 8;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СВЁРНУТОСТЬ КОЛОНКИ ГРУПП — ТРИ СОСТОЯНИЯ, А НЕ ДВА.
 *
 * `null` — НЕ ВЫБИРАЛИ. Тогда решает ширина, и порог берётся у эталона
 * дословно:
 *
 *     railFits = !phone && (s.w || 1440) >= 1360
 *     railOn   = s.rail === undefined ? railFits : (!phone && !!s.rail)
 *
 * То есть: 1360 и шире — колонка; уже — кнопка выбора группы в ряду
 * отборов. Выбрал человек сам — его выбор сильнее ширины, но на телефон
 * колонка не возвращается никогда: 216 пикселей это больше половины
 * экрана.
 *
 * ЗАЧЕМ ТРЕТЬЕ СОСТОЯНИЕ. Со «свёрнута: да/нет» умолчание пришлось бы
 * посчитать при первой отрисовке, замером ширины в коде, — а замер
 * в браузере отвечает уже ПОСЛЕ отрисовки, и колонка успевает мигнуть.
 * `null` отдаёт умолчание правилам оформления (`rk-rail-fits`
 * и `rk-rail-tight`), которые действуют с первого кадра.
 *
 * ПАМЯТЬ — В `localStorage`, а не на сервере: это свойство ЭКРАНА,
 * а не кабинета. Один и тот же человек за настольным и за ноутбуком
 * хочет разного, и синхронизировать это между устройствами значило бы
 * менять ему вид там, где он его не трогал.
 * ═══════════════════════════════════════════════════════════════════════
 */
export type Свёрнутость = boolean | null;

export function useСвёрнутостьГрупп(): [Свёрнутость, (свёрнута: boolean) => void] {
  const [свёрнута, setСвёрнута] = useState<Свёрнутость>(() => помнили());

  const выбрать = (v: boolean) => {
    setСвёрнута(v);
    try {
      localStorage.setItem(ПАМЯТЬ, v ? '1' : '0');
    } catch {
      /* Приватное окно и запрет хранилища — не повод падать: колонка
         просто забудет свой выбор до перезагрузки. */
    }
  };

  return [свёрнута, выбрать];
}

function помнили(): Свёрнутость {
  try {
    const было = localStorage.getItem(ПАМЯТЬ);
    return было === null ? null : было === '1';
  } catch {
    return null;
  }
}

function СтрокаГруппы({
  имя,
  счёт,
  выбрана,
  жать,
}: {
  имя: string;
  счёт: number;
  выбрана: boolean;
  жать(): void;
}) {
  return (
    <button
      type="button"
      data-d="строка-группы-в-колонке"
      title={имя}
      className={выбрана ? K.строкаГруппыВыбрана : K.строкаГруппыВКолонке}
      onClick={жать}
    >
      <span className={K.именаГруппыВыбрана}>{имя}</span>
      <span className={выбрана ? K.счётГруппыВыбран : K.счётГруппы}>{счёт}</span>
    </button>
  );
}

/**
 * Кнопка выбора группы — там, где колонки нет.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * НЕ ТОЛЬКО ТЕЛЕФОН. Так было до 26.08, и это расходилось с эталоном:
 * он ставит эту кнопку ВСЯКИЙ РАЗ, когда колонка не показана, —
 * и на 1280, где она не помещается, и на любом широком экране, где
 * колонку свернули руками (`railClosed: !railOn`).
 *
 * Пока кнопка была телефонной, свёрнутая колонка на настольном экране
 * оставляла человека без единого способа выбрать группу: полоска
 * с кареткой умела только развернуть колонку обратно.
 *
 * НЕ «ТА ЖЕ КОЛОНКА ПОУЖЕ». Двести шестнадцать пикселей колонки —
 * это больше половины экрана телефона и две графы таблицы на 1280.
 * ═══════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ВЫПАДЕНИЕ, КОТОРОЕ НЕ УЕЗЖАЕТ ЗА КРАЙ.
 *
 * Выпадение прибито к ЛЕВОМУ краю кнопки (`left: 0`) — так в эталоне,
 * и там оно всегда влезает: кнопка выбора группы стоит первой во втором
 * ряду отборов, слева, и справа от неё вся ширина экрана.
 *
 * У НАС КНОПКА ВСТАЁТ НЕ ТУДА, И НЕ ПО ОШИБКЕ. Ряд отборов —
 * переносящийся: поле поиска, чипы состояний, кнопка группы, кнопка
 * колонок. Где он перенесётся, решают ЧИСЛА НА ЧИПАХ. У стенда они
 * двузначные («все 36», «работают 21»), у нашей заглушки однозначные —
 * и тогда чипы уже на сто пикселей, поле поиска сжимается ещё
 * на семьдесят пять, кнопка группы влезает в ПЕРВЫЙ ряд и встаёт
 * у самого правого края. У живого кабинета так будет всякий раз, когда
 * кампаний меньше десяти в каждом состоянии.
 *
 * ЧТО ПРИ ЭТОМ ВИДНО ГЛАЗУ: выпадение шириной 264 открывается от кнопки
 * вправо, упирается в край карточки отборов (у неё `overflow`) и режется.
 * Пропадает правая треть — а в ней СЧЁТЧИКИ групп, ради которых список
 * наполовину и открывают. Ни сверка, ни типы этого не видят: узел
 * на месте, ширина у него честные 264, режет его предок. Нашлось глазом
 * на снимке «рядом» на 1280.
 *
 * ЛЕЧИМ НЕ РЯДОМ ОТБОРОВ, А ВЫПАДЕНИЕМ. Подгонять перенос под стенд
 * значило бы врать числами на чипах. Выпадение же просто прижимается
 * правым краем к правому краю кнопки, когда левым не влезает, — и там,
 * где влезает (то есть на данных стенда), не меняется ничего.
 * ═══════════════════════════════════════════════════════════════════════
 */
function обрезающий(узел: HTMLElement): DOMRect {
  let э: HTMLElement | null = узел.parentElement;
  while (э !== null) {
    const с = getComputedStyle(э);
    if (с.overflowX !== 'visible' || с.overflowY !== 'visible') return э.getBoundingClientRect();
    э = э.parentElement;
  }
  return new DOMRect(0, 0, document.documentElement.clientWidth, document.documentElement.clientHeight);
}

/**
 * `true` — выпадение не влезает вправо, и его надо прижать правым краем.
 *
 * Мерится ОТ КНОПКИ, а не от самого выпадения: выпадение к этому мигу
 * уже нарисовано слева, и его собственный правый край ответил бы
 * «влезаю» ровно после того, как мы его прижали, — вышел бы качающийся
 * ответ через раз.
 */
export function прижатьВправо(левыйКрайКнопки: number, ширинаВыпадения: number, правыйКрайРамки: number): boolean {
  return левыйКрайКнопки + ширинаВыпадения > правыйКрайРамки;
}

function useПрижатьВправо(
  кнопка: React.RefObject<HTMLElement | null>,
  выпадение: React.RefObject<HTMLElement | null>,
  открыто: boolean,
): boolean {
  const [прижать, setПрижать] = useState(false);
  useLayoutEffect(() => {
    if (!открыто) {
      setПрижать(false);
      return;
    }
    const к = кнопка.current;
    const в = выпадение.current;
    if (к === null || в === null) return;
    setПрижать(прижатьВправо(к.getBoundingClientRect().left, в.offsetWidth, обрезающий(в).right));
  }, [кнопка, выпадение, открыто]);
  return прижать;
}

export function ВыборГруппы({
  base,
  className,
  выбрана,
  выбрать,
  развернуть,
}: {
  base: string;
  /** Чем прятать, когда колонка показана: класс на самой обёртке. */
  className?: string;
  выбрана: string | null;
  выбрать(id: string | null): void;
  /**
   * «Показать колонкой» в выпадении. Нет довода — нет и пункта:
   * на телефоне колонки не бывает (`railCanOpen: !phone`).
   */
  развернуть?: () => void;
}) {
  const q = useГруппы(base);
  const [открыто, setОткрыто] = useState(false);
  const [поиск, setПоиск] = useState('');
  const [окно, setОкно] = useState(false);
  const узел = useRef<HTMLSpanElement>(null);
  const выпадение = useRef<HTMLSpanElement>(null);
  const прижать = useПрижатьВправо(узел, выпадение, открыто);

  useЗакрытьСнаружи(узел, () => setОткрыто(false), открыто);

  if (q.data === undefined || q.data.groups.length === 0) return null;

  const группы = поПорядку(q.data.groups);
  const безГруппы = q.data.всего - группы.reduce((a, г) => a + г.count, 0);
  const выбранная = группы.find((г) => г.id === выбрана);
  const имя =
    выбрана === null ? 'Все кампании' : выбрана === БЕЗ_ГРУППЫ ? 'Без группы' : (выбранная?.name ?? 'Все кампании');
  const счёт =
    выбрана === null ? q.data.всего : выбрана === БЕЗ_ГРУППЫ ? безГруппы : (выбранная?.count ?? 0);

  /* ПОИСК — ОТ ТОГО ЖЕ ЧИСЛА, ЧТО И В КОЛОНКЕ. Так в эталоне
     (`grpSearchOn`): на пяти группах поле только отнимает строку,
     на двадцати без него не найти. */
  const видимые =
    поиск.trim() === ''
      ? группы
      : группы.filter((г) => г.name.toLowerCase().includes(поиск.trim().toLowerCase()));

  return (
    <span
      ref={узел}
      className={className}
      style={{ position: 'relative', display: 'inline-flex', flex: '0 0 auto' }}
    >
      {/* ═══════════════════════════════════════════════════════════════
          ВЫБРАННАЯ ГРУППА КРАСИТ КНОПКУ ЦВЕТОМ МАРКИ — так в эталоне.

          Кнопка стояла тихой рамкой при любом отборе, и на 1024, где
          колонки групп нет, ничто на экране не говорило, ПОЧЕМУ строк
          три вместо сорока: отбор включён, а сказать об этом некому.
          Пять различий (рамка, фон, цвет, жирность и вместе с ними
          ширина) — это две частицы эталона, а не одна с перекраской.
          ═══════════════════════════════════════════════════════════ */}
      <button
        type="button"
        data-d="выбор-группы"
        className={выбрана === null ? K.выборГруппы : K.выборГруппыВыбран}
        onClick={() => setОткрыто(!открыто)}
      >
        <IconFolder size={13} />
        <span className={K.имяВыбораГруппы}>{имя}</span>
        <span className={K.счётВыбораГруппы}>{счёт}</span>
        {/* КАРЕТКА В КОНЦЕ — как в эталоне: без неё кнопка читается
            как метка, а не как выпадение, и её не нажимают. */}
        <IconChevron size={11} dir={открыто ? 'up' : 'down'} />
      </button>

      {открыто && (
        <span
          ref={выпадение}
          data-d="список-групп"
          className={K.выпадениеГрупп}
          style={прижать ? { left: 'auto', right: 0 } : undefined}
        >
          {/* ПОЛЕ ПОИСКА — ТА ЖЕ ЧАСТИЦА, ЧТО В КОЛОНКЕ: в стенде обе
              строки описаны одинаково, разнится лишь отступ снизу,
              которого у колонки нет (там его даёт обёртка ряда). */}
          {группы.length > ПОИСК_ОТ && (
            <input
              data-d="поиск-групп"
              className={K.поискГрупп}
              placeholder="Найти группу"
              style={{ marginBottom: 'var(--sp-2)' }}
              value={поиск}
              onChange={(e) => setПоиск(e.target.value)}
            />
          )}

          <span data-scroll="1" className={K.списокВыпаденияГрупп}>
            <ПунктГруппы
              имя="Все кампании"
              счёт={q.data.всего}
              выбрана={выбрана === null}
              жать={() => {
                выбрать(null);
                setОткрыто(false);
              }}
            />
            {видимые.map((г) => (
              <ПунктГруппы
                key={г.id}
                имя={г.name}
                счёт={г.count}
                выбрана={выбрана === г.id}
                жать={() => {
                  выбрать(г.id);
                  setОткрыто(false);
                }}
              />
            ))}
            {безГруппы > 0 && (
              <ПунктГруппы
                имя="Без группы"
                счёт={безГруппы}
                выбрана={выбрана === БЕЗ_ГРУППЫ}
                жать={() => {
                  выбрать(БЕЗ_ГРУППЫ);
                  setОткрыто(false);
                }}
              />
            )}
            {видимые.length === 0 && поиск.trim() !== '' && (
              <span className={D.нетТакойГруппы}>Такой группы нет</span>
            )}
          </span>

          {/* ═══════════════════════════════════════════════════════════
              ПОДВАЛ ВЫПАДЕНИЯ — ТОТ ЖЕ, ЧТО У КОЛОНКИ, И ЭТО НЕ ЛИШНЕЕ.

              Когда колонки нет, выпадение — единственное место, где
              вообще есть слово «группа»: без «Новой группы» завести
              первую группу с телефона было нечем, а без «Показать
              колонкой» свёрнутая колонка не возвращалась.
              ═══════════════════════════════════════════════════════ */}
          <span className={K.чертаВыпаденияГрупп} />

          <button
            type="button"
            data-d="кнопка-новая-группа"
            className={K.пунктНоваяГруппаВыпадения}
            onClick={() => {
              setОткрыто(false);
              setОкно(true);
            }}
          >
            <IconPlus size={13} /> Новая группа
          </button>

          {/* НА ТЕЛЕФОНЕ ПУНКТА НЕТ: `railCanOpen: !phone`. Колонка
              шириной 216 — больше половины экрана, и эталон её туда
              не пускает ни по какому нажатию. Прячет правило, а не
              условие в коде: замер ширины отвечает после отрисовки,
              и пункт успел бы мигнуть. */}
          {развернуть !== undefined && (
            <button
              type="button"
              data-d="кнопка-показать-колонкой"
              className={соединить(K.пунктПоказатьКолонкой, 'rk-only-wide')}
              onClick={() => {
                setОткрыто(false);
                развернуть();
              }}
            >
              <IconChevron size={13} dir="right" /> Показать колонкой
            </button>
          )}
        </span>
      )}

      {окно && <ОкноГрупп base={base} закрыть={() => setОкно(false)} />}
    </span>
  );
}

/**
 * Строка меню «В группу» — того, что открывается из полосы выделения.
 *
 * ЧАСТИЦА ЗДЕСЬ ДРУГАЯ, И ЭТО НЕ МЕЛОЧЬ: у эталона два выпадения
 * про группы, и строки у них разного роста (34 против
 * var(--groups-row)). Выбранной строки в этом меню не бывает вовсе —
 * им кладут кампании, а не отбирают.
 */
function ПунктМеню({ имя, счёт, жать }: { имя: string; счёт: number; жать(): void }) {
  return (
    <button type="button" className={K.пунктМенюГрупп} onClick={жать}>
      <span style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {имя}
      </span>
      <span style={{ color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>{счёт}</span>
    </button>
  );
}

/**
 * Строка выпадения выбора группы.
 *
 * ВЫБРАННАЯ ОКРАШЕНА, И ЭТО РАЗНЫЕ ЧАСТИЦЫ, А НЕ ОДНА. В эталоне
 * у выбранной строки цвет марки, подложка `--brand-wash` и полоска
 * слева; надеть её частицу на все строки значило бы объявить
 * выбранными сразу все — ровно та ошибка, которую стережёт
 * `kabinet/sostoyaniya.spec.ts`.
 */
function ПунктГруппы({
  имя,
  счёт,
  выбрана,
  жать,
}: {
  имя: string;
  счёт: number;
  выбрана: boolean;
  жать(): void;
}) {
  return (
    <button
      type="button"
      title={имя}
      className={выбрана ? K.пунктВыпаденияВыбран : K.пунктВыпаденияГрупп}
      onClick={жать}
    >
      <span className={K.имяПунктаВыпадения}>{имя}</span>
      <span className={выбрана ? K.счётВыпаденияВыбран : K.счётПунктаВыпадения}>{счёт}</span>
    </button>
  );
}

/**
 * «В группу» в полосе выделения.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * РАСКЛАДЫВАЮТ КАМПАНИИ ОТСЮДА, А НЕ ИЗ ОКНА НАСТРОЙКИ.
 *
 * Окно настройки — про сами группы: завести, переименовать, удалить.
 * Раскладка — про кампании, и делается она там же, где их выделяют:
 * человек отобрал «не тратят», выделил восемь строк и кладёт их
 * в «Разобрать». Заставить его после этого открыть окно и искать
 * те же восемь в списке значило бы отменить выделение.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function ВГруппу({
  base,
  ids,
  onDone,
}: {
  base: string;
  ids: string[];
  onDone(текст: string): void;
}) {
  const q = useГруппы(base);
  const обновить = useОбновить(base);
  const [открыто, setОткрыто] = useState(false);
  const [окно, setОкно] = useState(false);
  const узел = useRef<HTMLSpanElement>(null);

  useЗакрытьСнаружи(узел, () => setОткрыто(false), открыто);

  const положить = useMutation({
    mutationFn: (groupId: string | null) =>
      api<{ ok: true; сколько: number }>(`${base}/groups/assign`, {
        method: 'POST',
        body: { campaignIds: ids, groupId },
      }),
    onSuccess: (r, groupId) => {
      обновить();
      const имя = q.data?.groups.find((г) => г.id === groupId)?.name;
      onDone(
        groupId === null
          ? `Убрано из группы: ${кампанийСловами(r.сколько)}`
          : `В группу «${имя ?? ''}»: ${кампанийСловами(r.сколько)}`,
      );
      setОткрыто(false);
    },
    onError: (e: unknown) => onDone(строкаОшибки(e)),
  });

  if (q.data === undefined) return null;

  const группы = поПорядку(q.data.groups);

  return (
    <span ref={узел} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        data-d="кнопка-в-группу"
        className={K.кнопкаВГруппу}
        disabled={положить.isPending}
        onClick={() => setОткрыто(!открыто)}
      >
        В группу
        <IconChevron size={11} dir="up" />
      </button>

      {открыто && (
        <span data-d="меню-групп" className={K.менюГрупп}>
          <span data-scroll="1" className={K.списокМенюГрупп}>
            {группы.map((г) => (
              <ПунктМеню
                key={г.id}
                имя={г.name}
                счёт={г.count}
                жать={() => положить.mutate(г.id)}
              />
            ))}
          </span>

          <span className={K.чертаМенюГрупп} />

          <button
            type="button"
            className={K.пунктНоваяГруппа}
            onClick={() => {
              setОткрыто(false);
              setОкно(true);
            }}
          >
            <IconPlus size={13} /> Новая группа
          </button>

          {/* «УБРАТЬ ИЗ ГРУППЫ» ЕСТЬ ВСЕГДА, а не только когда все
              выделенные в группе. Проверять это значило бы читать
              группу каждой выделенной строки ради того, чтобы иногда
              спрятать пункт, — и человек, у которого он то есть,
              то нет, перестанет его искать. */}
          <button
            type="button"
            className={K.пунктУбратьИзГруппы}
            onClick={() => положить.mutate(null)}
          >
            Убрать из группы
          </button>
        </span>
      )}

      {окно && <ОкноГрупп base={base} закрыть={() => setОкно(false)} />}
    </span>
  );
}

/** Метка группы в строке кампании. */
export function МеткаГруппы({ имя }: { имя: string }) {
  return (
    <span data-d="метка-группы" className={K.меткаГруппы} title={`Группа «${имя}»`}>
      {имя}
    </span>
  );
}

/**
 * Окно «Группы кампаний»: завести, переименовать, удалить.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ИМЯ СОХРАНЯЕТСЯ ПО УХОДУ ИЗ ПОЛЯ, А НЕ ПО КНОПКЕ «СОХРАНИТЬ».
 *
 * Кнопка на каждой строке — это двадцать кнопок в окне из двадцати
 * групп, и девятнадцать из них всегда бесполезны. Уход из поля —
 * то же самое действие, что и нажатие, но без лишнего элемента:
 * человек поправил букву и щёлкнул мимо, и правка сохранилась.
 *
 * Общей кнопки «Сохранить всё» тоже нет: она обещает атомарность,
 * которой нет — переименования уходят по одному, и на третьем может
 * отказать сервер.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function ОкноГрупп({ base, закрыть }: { base: string; закрыть(): void }) {
  const q = useГруппы(base);
  const обновить = useОбновить(base);
  const [новая, setНовая] = useState('');
  const [беда, setБеда] = useState<string | null>(null);

  const завести = useMutation({
    mutationFn: (name: string) =>
      api<{ ok: true }>(`${base}/groups`, { method: 'POST', body: { name } }),
    onSuccess: () => {
      обновить();
      setНовая('');
      setБеда(null);
    },
    onError: (e: unknown) => setБеда(строкаОшибки(e)),
  });

  const переименовать = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api<{ ok: true }>(`${base}/groups/${id}`, { method: 'PATCH', body: { name } }),
    onSuccess: () => {
      обновить();
      setБеда(null);
    },
    onError: (e: unknown) => setБеда(строкаОшибки(e)),
  });

  const удалить = useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`${base}/groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      обновить();
      setБеда(null);
    },
    onError: (e: unknown) => setБеда(строкаОшибки(e)),
  });

  const группы = поПорядку(q.data?.groups ?? []);
  const занятые = группы.map((г) => г.name);
  const бедыНовой = новая.trim() === '' ? ['пусто'] : проверитьИмя(новая, занятые);
  /* Пустое поле — не беда, а исходное состояние: краснеть ему незачем. */
  const плохоеИмя = новая.trim() !== '' && бедыНовой.length > 0;

  return (
    /* ИМЯ УЗЛА — НА ЗАТЕМНЕНИИ, а не на окне: так у эталона
       (`data-d="окно-групп"` стоит на `position:fixed; inset:0`).
       У «окна целей» тот же стенд ставит имя на окне — он в этом
       непоследователен, и повторяем мы его, а не себя. */
    <Подложка onClose={закрыть} label="Группы кампаний" имя="окно-групп">
      <div className={K.окноГрупп}>
        <div className={K.шапкаОкнаГрупп}>
          <b className={K.названиеОкнаГрупп}>Группы кампаний</b>
          <button
            type="button"
            className={K.кнопкаЗакрытьГруппы}
            aria-label="Закрыть"
            onClick={закрыть}
          >
            <IconClose size={14} />
          </button>
        </div>

        <div className={K.телоОкнаГрупп}>
          <p className={K.поясненияГрупп}>
            Группа — свой список кампаний: отбор наверху таблицы показывает её одну, итоги
            и общие действия считаются по показанному. Кампания состоит в одной группе:
            если она была в другой, перейдёт в новую.
          </p>

          {группы.length > 0 && (
            <div data-scroll="1" className={K.окноГруппСписок}>
              {группы.map((г) => (
                <СтрокаНастройки
                  key={г.id}
                  группа={г}
                  занятые={занятые.filter((и) => и !== г.name)}
                  переименовать={(name) => переименовать.mutate({ id: г.id, name })}
                  удалить={() => удалить.mutate(г.id)}
                />
              ))}
            </div>
          )}

          <div className={K.блокНовойГруппы}>
            <span className={K.подписьНовойГруппы}>Новая группа</span>
            <div className={K.рядНовойГруппы}>
              <input
                data-d="поле-новой-группы"
                className={K.полеНовойГруппы}
                placeholder="например: Рекомендации"
                maxLength={ИМЯ_ДО}
                value={новая}
                onChange={(e) => {
                  setНовая(e.target.value);
                  setБеда(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && бедыНовой.length === 0) завести.mutate(новая);
                }}
              />
              <button
                type="button"
                data-d="кнопка-создать-группу"
                className={K.кнопкаСоздатьГруппу}
                disabled={бедыНовой.length > 0 || завести.isPending}
                onClick={() => завести.mutate(новая)}
              >
                {завести.isPending ? 'Завожу…' : 'Создать'}
              </button>
            </div>
            {/* ПОДПИСЬ ПОД ПОЛЕМ ГОВОРИТ ОДНО ИЗ ДВУХ: пока имя годное
                — зачем нужны группы; как только испортилось — что
                именно не так. Второе важнее, и оно вытесняет первое. */}
            <span
              className={K.подписьПодПолемГруппы}
              style={плохоеИмя ? { color: 'var(--bad-ink)' } : undefined}
            >
              {плохоеИмя ? бедыНовой.join('. ') : подписьНовой(группы.length > 0)}
            </span>
          </div>

          {/* ОТКАЗ ПОКАЗЫВАЕТСЯ ЗДЕСЬ, А НЕ ПЛАШКОЙ НАД ОКНОМ:
              человек смотрит в поле, куда набирал, и туда же должен
              прийти ответ. В эталоне отказа на этом окне нет вовсе —
              там ничего не отказывает. */}
          {беда !== null && (
            <p className={K.подписьПодПолемГруппы} style={{ color: 'var(--bad-ink)' }} role="alert">
              {беда}
            </p>
          )}
        </div>

        {/* «ГОТОВО» НИЧЕГО НЕ СОХРАНЯЕТ, И ЭТО НЕ ОПЛОШНОСТЬ.
            Всё в этом окне сохраняется сразу: имя — по уходу из поля,
            группа — по «Создать», удаление — по корзине. Кнопка
            закрывает окно, и подписана она честно: «Готово», а не
            «Сохранить». Так и в эталоне. */}
        <div className={K.подвалОкнаГрупп}>
          <button type="button" className={K.кнопкаГотовоГруппы} onClick={закрыть}>
            Готово
          </button>
        </div>
      </div>
    </Подложка>
  );
}

function СтрокаНастройки({
  группа,
  занятые,
  переименовать,
  удалить,
}: {
  группа: Группа;
  занятые: string[];
  переименовать(name: string): void;
  удалить(): void;
}) {
  const [имя, setИмя] = useState(группа.name);

  /* ВНЕШНЯЯ ПРАВКА ПОБЕЖДАЕТ НАБРАННОЕ, пока поле не тронуто:
     список перечитывается после каждой правки, и без этого поле
     показывало бы старое имя до перезагрузки окна. */
  useEffect(() => setИмя(группа.name), [группа.name]);

  return (
    <div data-d="строка-группы" className={K.строкаГруппыВОкне}>
      <input
        className={K.полеИмениГруппы}
        title="Имя группы"
        maxLength={ИМЯ_ДО}
        value={имя}
        onChange={(e) => setИмя(e.target.value)}
        onBlur={() => {
          const новое = имя.trim();
          if (новое === группа.name) return;
          if (проверитьИмя(новое, занятые).length > 0) {
            setИмя(группа.name);
            return;
          }
          переименовать(новое);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setИмя(группа.name);
        }}
      />
      <span className={K.счётГруппыВОкне}>{кампанийСловами(группа.count)}</span>
      <button
        type="button"
        data-d="кнопка-удалить-группу"
        className={K.кнопкаУдалитьГруппу}
        title="Удалить группу — кампании останутся"
        aria-label="Удалить группу"
        onClick={удалить}
      >
        <IconTrash size={14} />
      </button>
    </div>
  );
}

/** Закрыть по щелчку мимо. Меню без этого закрывается только повторным нажатием. */
function useЗакрытьСнаружи(
  узел: React.RefObject<HTMLElement | null>,
  закрыть: () => void,
  включено: boolean,
) {
  useEffect(() => {
    if (!включено) return;
    const мимо = (e: MouseEvent) => {
      if (узел.current !== null && !узел.current.contains(e.target as Node)) закрыть();
    };
    /* СЛУШАЕМ НА СПУСКЕ, А НЕ НА ЩЕЛЧКЕ: щелчок приходит после
       того, как нажатая кнопка уже сработала, и меню закрывалось
       бы после действия, а не вместо него. */
    document.addEventListener('mousedown', мимо);
    return () => document.removeEventListener('mousedown', мимо);
  }, [узел, закрыть, включено]);
}

/** Класс частицы плюс необязательный свой — без `undefined` в разметке. */
function соединить(частица: string, свой?: string): string {
  return свой === undefined ? частица : `${частица} ${свой}`;
}

function строкаОшибки(e: unknown): string {
  return (e as { message?: string })?.message ?? 'Не получилось';
}
